import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SlidePanel from '../shared/SlidePanel';
import TaxesHelp from './TaxesHelp';
import { loadTaxStore, saveTax, deleteTax, setCalculationMode, setTaxSettings, currentOwnerId, hydrateTaxStore } from './taxStore';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';
import { invalidateSellerTaxStore } from './sellerTax';

import '../marketing/campaigns/Campaigns.css';

// Tax-number presentation per country: label, example and a LIGHT format check
// (a warning, never a block — registries differ and salons know their number).
const TAX_NUMBER_FORMATS = {
  GB: { label: 'VAT number', placeholder: 'GB 123 4567 89', re: /^(GB\s?)?\d{3}\s?\d{4}\s?\d{2}(\s?\d{3})?$/i, hint: 'UK VAT numbers are GB followed by 9 digits (12 for branches).' },
  IE: { label: 'VAT number', placeholder: 'IE 1234567 WA', re: /^(IE\s?)?\d{7}[A-Z]{1,2}$/i, hint: 'Irish VAT numbers are IE, 7 digits and 1 or 2 letters.' },
  AU: { label: 'ABN', placeholder: '51 824 753 556', re: /^\d{2}\s?\d{3}\s?\d{3}\s?\d{3}$/, hint: 'An ABN is 11 digits.' },
  NZ: { label: 'GST number', placeholder: '123-456-789', re: /^\d{2,3}[- ]?\d{3}[- ]?\d{3}$/, hint: 'A GST (IRD) number is 8 or 9 digits.' },
  ZA: { label: 'VAT number', placeholder: '4123456789', re: /^4\d{9}$/, hint: 'A South African VAT number is 10 digits starting with 4.' },
  ES: { label: 'NIF / CIF', placeholder: 'B12345678', re: /^[A-Z0-9]\d{7}[A-Z0-9]$/i, hint: 'A NIF/CIF is 9 characters.' },
  IN: { label: 'GSTIN', placeholder: '22AAAAA0000A1Z5', re: /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d]Z[A-Z\d]$/i, hint: 'A GSTIN is 15 characters.' },
  US: { label: 'Tax ID (EIN)', placeholder: '12-3456789', re: /^\d{2}-?\d{7}$/, hint: 'An EIN is 9 digits.' },
  CA: { label: 'GST/HST number', placeholder: '123456789 RT0001', re: /^\d{9}(\s?RT\s?\d{4})?$/i, hint: 'A GST/HST number is 9 digits, usually followed by RT0001.' }
};
const taxNumberFormat = (code) => TAX_NUMBER_FORMATS[code] || { label: 'VAT / tax number', placeholder: 'e.g. GB 123 4567 89', re: null, hint: '' };

// The site this page is editing (the number is stored per location).
const currentSiteName = () => {
  try {
    const c = JSON.parse(sessionStorage.getItem('ml:sites') || 'null'); const id = localStorage.getItem('salonId');
    const s = c && c.data && c.data.sites ? c.data.sites.find((x) => String(x.salonId || x._id || x.id) === String(id)) : null;
    return s ? (s.salonName || s.name || null) : null;
  } catch (e) { return null; }
};

// Multi-location accounts get a per-rate location scope (docs/tax-money-path.md §4c).
const isMultiLocation = () => {
  try { const c = JSON.parse(sessionStorage.getItem('ml:sites') || 'null'); return !!(c && c.data && c.data.sites && c.data.sites.length > 1); } catch (e) { return false; }
};

// One category row in "Category defaults": a default applies to every item in
// the category now and to items added to it later. Unticking all = tax-free.
const CategoryTaxRow = ({ domain, cat, taxes, onSaved }) => {
  const [taxIds, setTaxIds] = useState(cat.taxIds);   // null = no default (items use type defaults / their own picks)
  const [saving, setSaving] = useState(false);
  const save = (next) => {
    setTaxIds(next); setSaving(true);
    marketingBackend.taxCategoryOverride(domain, cat.name, next)
      .then((r) => { invalidateSellerTaxStore(); onSaved(r); })
      .catch((e) => console.error('category tax save failed:', e.message))
      .finally(() => setSaving(false));
  };
  const usingDefault = taxIds === null;
  const applicable = taxes.filter((t) => t.enabled && t.appliesTo[domain]);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 2px', borderTop: '1px solid #F2F2F7' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: ink }}>{cat.name}</span>
        <span style={{ display: 'block', fontSize: 12, color: muted, marginTop: 2 }}>
          {usingDefault ? `Uses your ${domain === 'products' ? 'product' : 'service'} defaults${applicable.length ? ` (${applicable.map((t) => `${t.name} ${t.rate}%`).join(', ')})` : ' (none apply)'}` : (taxIds.length ? `Category default: ${taxes.filter((t) => taxIds.includes(t.id)).map((t) => `${t.name} ${t.rate}%`).join(', ')}` : 'Category default: no tax')}
          {saving ? ' · saving…' : ''}
        </span>
        {!usingDefault && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 8 }}>
            {taxes.filter((t) => t.enabled).map((t) => (
              <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: ink, cursor: 'pointer' }}>
                <input type="checkbox" checked={taxIds.includes(t.id)} onChange={() => save(taxIds.includes(t.id) ? taxIds.filter((id) => id !== t.id) : [...taxIds, t.id])} style={{ width: 15, height: 15, accentColor: '#29282D' }} />
                {t.name} <span style={{ color: muted }}>{t.rate}%</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <button type="button" onClick={() => save(usingDefault ? applicable.map((t) => t.id) : null)} style={{ padding: '7px 12px', border: `1px solid ${line}`, borderRadius: 9, background: '#fff', fontSize: 12.5, fontWeight: 600, color: ink, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {usingDefault ? 'Set for category' : 'Use defaults'}
      </button>
    </div>
  );
};

/**
 * Catalogue › Taxes (content)
 * Named tax rates, creator-scoped: the salon owner's taxes cover salon
 * items and employed staff; a self-employed stylist manages their own.
 * Calculation mode is a single engine-level choice: inclusive (UK VAT,
 * tax inside the displayed price) or exclusive (US model, added on top).
 */

const ink = '#1A1A1E';
const muted = '#8E8E93';
const line = '#ECECF0';

const DOMAINS = [
  { id: 'services', label: 'Services' },
  { id: 'products', label: 'Products' },
  { id: 'vouchers', label: 'Vouchers' },
];

const ModeRow = ({ selected, disabled, title, sub, pill, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onSelect}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', textAlign: 'left',
      padding: '14px 16px', marginTop: 10,
      border: `1.5px solid ${selected ? '#29282D' : line}`, borderRadius: 12,
      background: '#fff', cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
      opacity: disabled ? 0.55 : 1, transition: 'border-color 0.15s'
    }}
  >
    <span style={{
      width: 18, height: 18, borderRadius: '50%',
      border: selected ? '6px solid #29282D' : '2px solid #DEDEE2',
      backgroundColor: '#fff', boxSizing: 'border-box', flexShrink: 0, marginTop: 1,
      transition: 'border 0.15s'
    }} />
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: ink }}>{title}</span>
        {pill ? (
          <span style={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
            color: muted, background: '#F2F2F7', borderRadius: 100, padding: '3px 9px'
          }}>
            {pill}
          </span>
        ) : null}
      </span>
      <span style={{ display: 'block', fontSize: 12.5, color: muted, lineHeight: 1.5, marginTop: 3 }}>{sub}</span>
    </span>
  </button>
);

const Toggle = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    onClick={onChange}
    style={{
      width: 44, height: 26, borderRadius: 100, border: 'none', cursor: 'pointer', padding: 2,
      background: checked ? '#007EA0' : '#DEDEE2', transition: 'background 0.15s', flexShrink: 0
    }}
  >
    <span style={{
      display: 'block', width: 22, height: 22, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'transform 0.15s',
      transform: checked ? 'translateX(18px)' : 'translateX(0)'
    }} />
  </button>
);

export default function TaxesContent() {
  const ownerId = currentOwnerId();
  const user = useSelector((state) => state.user);
  const isSelfEmployed = Number(user?.userInfo?.hairdresser?.employmentTypeId) === 2 && user?.userInfo?.hairdresser?.isSalonOwner !== 1;
  const [taxes, setTaxes] = useState(() => loadTaxStore(ownerId).taxes);
  const [calcMode, setCalcMode] = useState(() => loadTaxStore(ownerId).calculationMode || 'inclusive');
  const [taxOn, setTaxOn] = useState(() => loadTaxStore(ownerId).enabled === true);
  const [taxNumber, setTaxNumber] = useState(() => loadTaxStore(ownerId).taxNumber || '');
  const [switchError, setSwitchError] = useState('');
  // Hydrate from the backend on mount (localStorage is only the cache now).
  useEffect(() => {
    let alive = true;
    hydrateTaxStore(ownerId).then((store) => {
      if (!alive) return;
      setTaxes(store.taxes);
      setCalcMode(store.calculationMode || 'inclusive');
      setTaxOn(store.enabled === true);
      setTaxNumber(store.taxNumber || '');
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [editing, setEditing] = useState(null); // tax object | 'new'
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftError, setDraftError] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [preset, setPreset] = useState(null);          // { countryName, preset } from the salon's country
  const [presetBusy, setPresetBusy] = useState(false);
  const [categories, setCategories] = useState(null);  // { services: [...], products: [...] }
  useEffect(() => {
    let alive = true;
    marketingBackend.taxPresets().then((p) => { if (alive) setPreset(p); }).catch(() => {});
    marketingBackend.taxCategories().then((c) => { if (alive) setCategories(c); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const applyPreset = async () => {
    if (!preset || !preset.preset) return;
    setPresetBusy(true);
    try {
      for (const r of preset.preset.rates) {
        if (r.rate === null || r.rate === undefined) continue;   // rates that vary by state are entered by hand
        saveTax(ownerId, { name: r.name, rate: r.rate, appliesTo: r.appliesTo, enabled: true, scope: 'all' });
      }
      setCalcMode(preset.preset.calculationMode); setCalculationMode(ownerId, preset.preset.calculationMode);
      const store = await hydrateTaxStore(ownerId);
      setTaxes(store.taxes);
    } finally { setPresetBusy(false); }
  };
  const refreshCategories = () => marketingBackend.taxCategories().then(setCategories).catch(() => {});

  const openEditor = (tax) => {
    setDraft(tax === 'new'
      ? { name: '', rate: '', appliesTo: { services: true, products: true, vouchers: false }, enabled: true }
      : { ...tax, appliesTo: { ...tax.appliesTo } });
    setDraftError('');
    setEditing(tax === 'new' ? 'new' : tax.id);
  };

  const handleSaveDraft = () => {
    const rate = parseFloat(draft.rate);
    if (!draft.name.trim()) { setDraftError('Give this tax a name, like VAT.'); return; }
    if (Number.isNaN(rate) || rate < 0 || rate > 100) { setDraftError('Enter a rate between 0 and 100.'); return; }
    setTaxes(saveTax(ownerId, { ...draft, name: draft.name.trim(), rate: Math.round(rate * 100) / 100 }));
    setEditing(null);
  };

  const toggleEnabled = (tax) => {
    setTaxes(saveTax(ownerId, { ...tax, enabled: !tax.enabled }));
  };

  const anyEnabled = taxes.some((t) => t.enabled);

  return (
    <div className="cmp-wrap" style={{ maxWidth: 760 }}>
      <div className="cmp-head">
        <div className="cmp-head-text">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className="cmp-title">Taxes</h1>
            <button type="button" className="pv2-help-btn" onClick={() => setHelpOpen(true)} aria-label="How taxes work" title="How taxes work">?</button>
          </div>
          <p className="cmp-subtitle">
            Name your tax rates and choose what they apply to. Each rate can also be set per item from the service, product and voucher editors.
          </p>
        </div>
      </div>

      {/* Master switch — docs/tax-money-path.md §1 #10. Rates and the picker are
          saved regardless; nothing is taxed at the till until this is on. */}
      <div style={{ background: '#fff', border: `1px solid ${line}`, borderRadius: 14, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: ink, margin: 0 }}>Charge tax on your sales</h2>
            <p style={{ fontSize: 13, color: muted, margin: '6px 0 0' }}>
              {taxOn
                ? 'Your rates apply to every sale from now on. Commission and splits are calculated on prices excluding tax; the tax stays with whoever made the sale.'
                : 'Off. Your rates are saved but nothing is taxed. Receipts, splits and reports are exactly as they were.'}
            </p>
          </div>
          <Toggle
            checked={taxOn}
            onChange={() => {
              const next = !taxOn;
              const number = taxNumber.trim();
              // Only a VAT-registered business can charge VAT, and the number
              // has to be on the receipt. Switching on needs it; switching off
              // (or keeping an existing setup) never does. The server checks too.
              if (next && !number) {
                setSwitchError(`Add your ${taxNumberFormat(preset && preset.countryCode).label} below first. Only a registered business can charge tax, and the number has to show on receipts.`);
                return;
              }
              setSwitchError('');
              setTaxOn(next);
              setTaxSettings(ownerId, next ? { enabled: true, taxNumber: number } : { enabled: false }, (e) => {
                setTaxOn(!next);
                setSwitchError(e.message);
              });
            }}
            ariaLabel="Charge tax on your sales"
          />
        </div>
        {switchError && (
          <p role="alert" style={{ fontSize: 12.5, color: '#B14438', margin: '12px 0 0' }}>{switchError}</p>
        )}
        {(taxOn || switchError || taxNumber) && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #F2F2F7' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
              {taxNumberFormat(preset && preset.countryCode).label}
              {isMultiLocation() && currentSiteName() ? <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}> · {currentSiteName()}</span> : null}
            </label>
            <input
              value={taxNumber}
              onChange={(e) => { setTaxNumber(e.target.value); if (switchError) setSwitchError(''); }}
              onBlur={() => setTaxSettings(ownerId, { taxNumber: taxNumber.trim() || null })}
              placeholder={taxNumberFormat(preset && preset.countryCode).placeholder}
              maxLength={40}
              style={{ width: '100%', maxWidth: 320, fontSize: 14, padding: '10px 12px', border: `1px solid ${line}`, borderRadius: 10, color: ink }}
            />
            {(() => {
              const f = taxNumberFormat(preset && preset.countryCode); const v = taxNumber.trim();
              if (v && f.re && !f.re.test(v)) return <p style={{ fontSize: 11.5, color: '#B14438', margin: '8px 0 0' }}>That doesn't look like a valid {f.label}. {f.hint} It will still be printed as typed.</p>;
              return null;
            })()}
            <p style={{ fontSize: 11.5, color: muted, margin: '8px 0 0' }}>
              {taxOn ? '' : 'Needed before you can switch tax on. '}Printed on receipts, order emails and the VAT report for sales that carried tax.{isMultiLocation() ? ' Each location keeps its own number.' : ''}
            </p>
          </div>
        )}
      </div>
      {/* Suggested rates for the salon's country — one click, never automatic */}
      {preset && preset.preset && taxes.length === 0 && (
        <div style={{ background: '#F5FAFB', border: '1px solid #CFE7EE', borderRadius: 14, padding: 20, marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: ink, margin: '0 0 4px' }}>Suggested for {preset.preset.country}</h2>
          <p style={{ fontSize: 13, color: muted, margin: '0 0 12px' }}>
            Prices shown {preset.preset.calculationMode === 'inclusive' ? 'include tax' : 'exclude tax (added at checkout)'}. Check the rates with your accountant. You can edit or delete any of them afterwards.
          </p>
          <ul style={{ margin: '0 0 14px', paddingLeft: 18, fontSize: 13, color: ink, lineHeight: 1.7 }}>
            {preset.preset.rates.map((r) => (
              <li key={r.name}><strong>{r.name}</strong>{r.rate !== null && r.rate !== undefined ? ` ${r.rate}%` : ', enter your rate'}<span style={{ color: muted }}> · {r.note}</span></li>
            ))}
          </ul>
          <button type="button" disabled={presetBusy} onClick={applyPreset} style={{ padding: '10px 18px', backgroundColor: '#29282D', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
            {presetBusy ? 'Adding…' : 'Use these rates'}
          </button>
        </div>
      )}

      {/* Tax rates */}
      <div style={{ background: '#fff', border: `1px solid ${line}`, borderRadius: 14, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: ink, margin: 0 }}>Tax rates</h2>
          <button
            type="button"
            onClick={() => openEditor('new')}
            style={{ padding: '10px 18px', backgroundColor: '#29282D', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Add tax rate
          </button>
        </div>
        <p style={{ fontSize: 13, color: muted, margin: '0 0 14px' }}>
          {isSelfEmployed
            ? 'These are yours alone: you are the seller on your own sales, so the salon\'s rates never apply to them.'
            : 'These are yours alone. Self-employed stylists manage their own rates from their account.'}
        </p>

        {taxes.map((tax, i) => (
          <div
            key={tax.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 2px',
              borderTop: i ? '1px solid #F2F2F7' : 'none', cursor: 'pointer'
            }}
            onClick={() => openEditor(tax)}
          >
            <span style={{
              minWidth: 52, textAlign: 'center', fontSize: 13, fontWeight: 800, letterSpacing: '-0.2px',
              color: tax.enabled ? '#007EA0' : muted,
              background: tax.enabled ? 'rgba(0,126,160,0.1)' : '#F2F2F7',
              borderRadius: 9, padding: '7px 8px', flexShrink: 0
            }}>
              {tax.rate}%
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: tax.enabled ? ink : muted }}>{tax.name}</span>
              <span style={{ display: 'block', fontSize: 12, color: muted, marginTop: 2 }}>
                {DOMAINS.filter((d) => tax.appliesTo[d.id]).map((d) => d.label).join(', ') || 'Not applied by default'}
              </span>
            </span>
            <span onClick={(e) => e.stopPropagation()}>
              <Toggle checked={tax.enabled} onChange={() => toggleEnabled(tax)} ariaLabel={`${tax.name} enabled`} />
            </span>
          </div>
        ))}

        {!anyEnabled && (
          <p style={{ fontSize: 12.5, fontWeight: 600, color: '#A05A1F', margin: '12px 0 0' }}>
            No active taxes. Sales will not include tax.
          </p>
        )}
      </div>

      {/* Calculation mode */}
      <div style={{ background: '#fff', border: `1px solid ${line}`, borderRadius: 14, padding: 24 }} role="radiogroup" aria-label="Tax calculation">
        <h2 style={{ fontSize: 17, fontWeight: 700, color: ink, margin: 0 }}>Tax calculation</h2>
        <p style={{ fontSize: 13, color: muted, margin: '6px 0 4px' }}>
          How your tax rates relate to the prices clients see.
        </p>

        <ModeRow
          selected={calcMode === 'inclusive'}
          title="Prices include tax"
          sub="Clients pay the price you display. A £10.00 service is £8.33 plus £1.67 VAT, since the 20% is charged on the £8.33 before tax."
          onSelect={() => { setCalcMode('inclusive'); setCalculationMode(ownerId, 'inclusive'); }}
        />
        <ModeRow
          selected={calcMode === 'exclusive'}
          title="Prices exclude tax"
          sub="Tax is added on top of your displayed prices at checkout. A £10.00 service with 20% tax charges the client £12.00. This is the US model."
          onSelect={() => { setCalcMode('exclusive'); setCalculationMode(ownerId, 'exclusive'); }}
        />

        <p style={{ fontSize: 11.5, color: muted, margin: '14px 0 0', paddingTop: 14, borderTop: '1px solid #F2F2F7' }}>
          Changes apply to new sales only. Past invoices keep the rates from the time of sale.
        </p>
      </div>

      {helpOpen && <TaxesHelp onClose={() => setHelpOpen(false)} />}

      {/* Editor panel */}
      {editing && draft && (
        <SlidePanel
          title={editing === 'new' ? 'New tax rate' : 'Edit tax rate'}
          width={400}
          onClose={() => setEditing(null)}
          footer={(
            <>
              {editing !== 'new' ? (
                <button
                  type="button"
                  className="sp-btn-secondary"
                  style={{ color: '#B14438', borderColor: 'rgba(177,68,56,0.3)' }}
                  onClick={() => { setEditing(null); setConfirmDeleteId(draft.id); }}
                >
                  Delete
                </button>
              ) : (
                <button type="button" className="sp-btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              )}
              <button type="button" className="sp-btn-primary" onClick={handleSaveDraft}>
                {editing === 'new' ? 'Add tax rate' : 'Save changes'}
              </button>
            </>
          )}
        >
          <div className="sp-form">
            <div className="sp-field">
              <label className="sp-label">Name</label>
              <input
                className="sp-input"
                type="text"
                value={draft.name}
                onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, name: v })); }}
                placeholder="e.g. VAT"
              />
            </div>
            <div className="sp-field">
              <label className="sp-label">Rate</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="sp-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={draft.rate}
                  onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, rate: v })); }}
                  placeholder="20"
                  style={{ paddingRight: 34 }}
                />
                <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14, fontWeight: 600, color: muted }}>%</span>
              </div>
            </div>

            <div className="sp-field">
              <label className="sp-label">Apply by default to</label>
              {DOMAINS.map((d) => (
                <div key={d.id} className="sp-toggle-row" style={{ marginTop: 8 }}>
                  <span className="sp-toggle-label" style={{ fontWeight: 600, color: ink }}>{d.label}</span>
                  <Toggle
                    checked={!!draft.appliesTo[d.id]}
                    onChange={() => setDraft((dr) => ({ ...dr, appliesTo: { ...dr.appliesTo, [d.id]: !dr.appliesTo[d.id] } }))}
                    ariaLabel={`Apply to ${d.label}`}
                  />
                </div>
              ))}
              <p className="sp-hint" style={{ marginTop: 10 }}>
                Most UK vouchers are not taxed when sold, so vouchers default to off. You can still set tax per item from each editor.
              </p>
            </div>

            {isMultiLocation() && (
              <div className="sp-field">
                <label className="sp-label">Where it applies</label>
                {[['all', 'All my locations'], ['site', 'This location only']].map(([v, label]) => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                    <input type="radio" name="tax-scope" checked={(draft.scope || 'all') === v} onChange={() => setDraft((d) => ({ ...d, scope: v }))} style={{ accentColor: '#29282D' }} />
                    <span style={{ fontSize: 13.5, color: ink }}>{label}</span>
                  </label>
                ))}
                <p style={{ fontSize: 12, color: muted, margin: '4px 0 0' }}>Sites in a different tax position keep their own rates.</p>
              </div>
            )}

            {draftError ? <p className="sp-error">{draftError}</p> : null}
          </div>
        </SlidePanel>
      )}

      {/* Category defaults — a default set here is applied to every item in the
          category now and inherited by items added later (docs/tax-money-path.md §4b). */}
      {taxOn && taxes.some((t) => t.enabled) && categories && (categories.services.length + categories.products.length) > 0 && (
        <div style={{ background: '#fff', border: `1px solid ${line}`, borderRadius: 14, padding: 24, marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: ink, margin: '0 0 6px' }}>Category defaults</h2>
          <p style={{ fontSize: 13, color: muted, margin: '0 0 8px' }}>
            Set a rate for a whole category. It applies to everything in it today and to anything you add to it later. Individual items can still be changed from their editor.
          </p>
          {categories.services.length > 0 && <p style={{ fontSize: 12, fontWeight: 600, color: muted, letterSpacing: '0.04em', textTransform: 'uppercase', margin: '14px 0 2px' }}>Service categories</p>}
          {categories.services.map((c) => <CategoryTaxRow key={`s:${c.key}`} domain="services" cat={c} taxes={taxes} onSaved={refreshCategories} />)}
          {categories.products.length > 0 && <p style={{ fontSize: 12, fontWeight: 600, color: muted, letterSpacing: '0.04em', textTransform: 'uppercase', margin: '14px 0 2px' }}>Product categories</p>}
          {categories.products.map((c) => <CategoryTaxRow key={`p:${c.key}`} domain="products" cat={c} taxes={taxes} onSaved={refreshCategories} />)}
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100020, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setConfirmDeleteId(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, background: '#fff', borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: ink, margin: '0 0 6px' }}>Delete this tax rate?</h3>
            <p style={{ fontSize: 13.5, color: muted, lineHeight: 1.55, margin: '0 0 18px' }}>
              Removing "{taxes.find((t) => t.id === confirmDeleteId)?.name}" stops it applying to new sales. Past sales are not affected.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={() => setConfirmDeleteId(null)} style={{ flex: 1, padding: 11, border: `1px solid ${line}`, borderRadius: 10, background: '#fff', fontSize: 13.5, fontWeight: 600, color: ink, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setTaxes(deleteTax(ownerId, confirmDeleteId)); setConfirmDeleteId(null); }}
                style={{ flex: 1, padding: 11, border: 'none', borderRadius: 10, background: '#B14438', fontSize: 13.5, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

